import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useNotification } from '../context/NotificationContext';
import { subscriptionAPI, doctorAPI, paymentAPI } from '../utils/api';
import PaymentGateway from '../components/PaymentGateway';
import SkeletonLoader from '../components/SkeletonLoader';
import appleIAP, { PRODUCT_IDS } from '../services/appleIAP';
import { isIOS, handleIOSSubscription, handleIOSPayPerUse } from '../utils/iapPayment';
import { mapPlanToProductId } from '../utils/iapPayment';

const Subscription = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, refreshProfile } = useAuth();
  const { showSuccess, showError } = useNotification();
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showPayment, setShowPayment] = useState(false);
  const [blueTickPrice, setBlueTickPrice] = useState(0);
  const [isBlueTickPayment, setIsBlueTickPayment] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [iapFinished, setIapFinished] = useState(false);

  const role = searchParams.get('role') || user?.role || 'patient';
  const queryUserId = searchParams.get('userId');
  const userId = queryUserId || user?._id || localStorage.getItem('temp_userId');

  const [appleProducts, setAppleProducts] = useState({});
  const [payingPlanId, setPayingPlanId] = useState(null);
  const iosDevice = isIOS();

  useEffect(() => {
    fetchSubscriptionPlans();
    if (user?.role === 'doctor') {
      fetchBlueTickPrice();
    }
    if (iosDevice) {
      initIAP();
    }
  }, [role, user]);

  const initIAP = async () => {
    try {
      await appleIAP.initialize();
      const prods = appleIAP.getProducts();
      const prodMap = {};
      prods.forEach(p => { prodMap[p.id] = p; });
      setAppleProducts(prodMap);
    } catch (err) {
      console.error('IAP init error:', err);
    } finally {
      setIapFinished(true);
    }
  };

  const fetchBlueTickPrice = async () => {
    try {
      const res = await doctorAPI.getBlueTickPrice();
      if (res.data.success) {
        setBlueTickPrice(res.data.data);
      }
    } catch (error) {
      console.error("Error fetching blue tick price:", error);
    }
  };

  const fetchSubscriptionPlans = async () => {
    console.log('🔄 Fetching plans...', {
      role,
      tokenPresent: !!localStorage.getItem('token'),
      userId
    });
    try {
      const res = await subscriptionAPI.getPlans(role || 'patient');
      console.log('✅ Plans response:', res.data);
      if (res.data?.success) {
        setPlans(res.data.data);
      }
    } catch (error) {
      console.error('❌ Error fetching plans:', error);
      showError('Failed to load subscription plans');
    } finally {
      setLoading(false);
    }
  };

  const handlePlanSelect = async (plan) => {
    if (iosDevice) {
      setPayingPlanId(plan._id);
      const productId = mapPlanToProductId(plan, role);
      if (!productId) {
        showError('Apple product not found for this plan');
        setPayingPlanId(null);
        return;
      }
      await handleIOSSubscription({
        plan: { ...plan, appleProductId: productId },
        role: role,
        onSuccess: async () => {
          showSuccess('Subscription activated successfully!');
          setPayingPlanId(null);
          await handlePostPaymentSuccess();
        },
        onError: (msg) => {
          showError(msg);
          setPayingPlanId(null);
        },
        onFinally: () => setPayingPlanId(null),
      });
      return;
    }
    setSelectedPlan(plan);
    setShowPayment(true);
  };

  const handleBlueTickSelect = async () => {
    if (iosDevice) {
      setIsBlueTickPayment(true);
      setPayingPlanId('blueTick');
      try {
        await handleIOSPayPerUse({
          productId: PRODUCT_IDS.doctor.blueTick,
          onSuccess: async (response) => {
            const res = await doctorAPI.requestBlueTick(response.transactionId);
            if (res.data.success) {
              showSuccess("Blue tick request submitted! Admin will review it.");
              setIsBlueTickPayment(false);
              setPayingPlanId(null);
              await handlePostPaymentSuccess();
            } else {
              showError(res.data.message || 'Failed to submit blue tick request');
              setPayingPlanId(null);
            }
          },
          onError: (msg) => {
            showError(msg);
            setPayingPlanId(null);
          },
          onFinally: () => setPayingPlanId(null),
        });
      } catch (err) {
        showError("Failed to submit blue tick request");
        setPayingPlanId(null);
      }
      return;
    }
    setIsBlueTickPayment(true);
    setShowPayment(true);
  };

  const handlePostPaymentSuccess = async () => {
    // Refresh profile to update subscription status locally
    await refreshProfile();

    setTimeout(() => {
      if (role && role !== 'patient') {
        navigate(`/onboarding/profile?userId=${userId}&role=${role}`);
      } else {
        navigate('/patient/home');
      }
    }, 1000);
  };

  const handlePaymentSuccess = async (response) => {
    if (isBlueTickPayment) {
      try {
        const res = await doctorAPI.requestBlueTick(response.razorpay_payment_id);
        if (res.data.success) {
          showSuccess("Blue tick request submitted! Admin will review it.");
          setIsBlueTickPayment(false);
          setShowPayment(false);
          setSelectedPlan(null);
          handlePostPaymentSuccess();
        } else {
          showError(res.data.message || 'Failed to submit blue tick request');
        }
      } catch (error) {
        showError("Failed to submit blue tick request");
      }
    } else {
      try {
        const verifyRes = await paymentAPI.verifySubscription({
          razorpay_order_id: response.razorpay_order_id,
          razorpay_payment_id: response.razorpay_payment_id,
          razorpay_signature: response.razorpay_signature,
          plan_id: selectedPlan._id
        });
        if (verifyRes.data?.success) {
          showSuccess('Subscription activated!');
          setShowPayment(false);
          setSelectedPlan(null);
          handlePostPaymentSuccess();
        } else {
          showError('Account update failed. Please contact support.');
        }
      } catch (error) {
        showError('Failed to verify payment: ' + error.message);
      }
    }
  };

  const handlePaymentError = (error) => {
    showError(`Payment failed: ${error}`);
    setShowPayment(false);
    setSelectedPlan(null);
  };

  const handlePaymentClose = () => {
    setShowPayment(false);
    setSelectedPlan(null);
  };

  if (loading) {
    return (
      <div className="p-6">
        <SkeletonLoader type="card" count={3} />
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto space-y-10 py-12 px-4">
      <div className="text-center space-y-4 relative z-10">
        <h1 className="text-4xl md:text-5xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-yellow-500 via-yellow-400 to-amber-500">
          Choose Your Plan
        </h1>
        <p className="text-gray-400 text-lg max-w-2xl mx-auto">
          Select a subscription plan that best fits your needs and unlock premium features.
        </p>
        {iosDevice && (
          <button
            onClick={async () => {
              try {
                await appleIAP.restorePurchases();
                showSuccess('Purchases restored!');
                await refreshProfile();
              } catch (err) {
                showError(err.message || 'Failed to restore purchases');
              }
            }}
            className="mt-4 text-sm font-medium text-yellow-500/80 hover:text-yellow-500 transition-colors underline underline-offset-4"
          >
            Restore Purchases
          </button>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-8 relative z-10">
        {plans.length === 0 ? (
          <div className="col-span-full text-center py-16 bg-white/5 rounded-3xl border border-white/10 backdrop-blur-md">
            <p className="text-xl text-gray-300">No subscription plans available yet.</p>
            <p className="text-sm text-gray-500 mt-3">Please check back later.</p>
          </div>
        ) : (
          plans.map((plan) => {
            const applePrice = iosDevice ? (appleProducts[mapPlanToProductId(plan, role)]?.localizedPrice || (iapFinished ? `₹${plan.ios_amount || plan.amount}` : 'Loading...')) : null;
            const isProcessing = payingPlanId === plan._id;
            const isSubscribed = user?.subscription_status === 'active';

            return (
              <div
                key={plan._id}
                className="group relative bg-white/5 rounded-3xl p-8 border border-white/10 shadow-xl backdrop-blur-xl transition-all duration-300 hover:scale-[1.02] hover:border-yellow-500/30 hover:shadow-yellow-500/10 overflow-hidden flex flex-col"
              >
                <div className="absolute top-0 right-0 w-32 h-32 bg-yellow-500/10 rounded-full blur-3xl -mr-10 -mt-10 transition-opacity group-hover:opacity-100 opacity-0"></div>

                <div className="mb-8">
                  <h3 className="text-2xl font-black text-white mb-2">{plan.name}</h3>
                  {plan.description && (
                    <p className="text-gray-400 text-sm leading-relaxed min-h-[40px]">{plan.description}</p>
                  )}
                </div>

                <div className="mb-8">
                  <p className="text-4xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-yellow-500 to-amber-600 tracking-tight">
                    {iosDevice ? applePrice : `₹${Math.round(plan.amount * 1.18)}`}
                  </p>
                  <p className="text-sm text-gray-500 mt-2 font-medium tracking-wide">
                    {iosDevice ? 'App Store Subscription' : '+ 18% GST Included'}
                  </p>
                </div>

                <div className="flex-grow space-y-4 mb-8">
                  <h4 className="text-sm font-bold text-gray-300 uppercase tracking-wider">Plan Includes</h4>
                  <ul className="space-y-3">
                    <li className="flex items-center text-gray-400">
                      <svg className="w-5 h-5 text-yellow-500 mr-3 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      Valid for <span className="font-medium text-gray-200 ml-1">{plan.duration_days ?? 30} Days</span>
                    </li>
                    {plan.features?.map((feature, index) => (
                      <li key={index} className="flex items-start text-gray-400">
                        <svg className="w-5 h-5 text-yellow-500 mr-3 shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                        </svg>
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                <button
                  onClick={() => handlePlanSelect(plan)}
                  disabled={isSubscribed || payingPlanId !== null || (iosDevice && !appleProducts[mapPlanToProductId(plan, role)] && !iapFinished)}
                  className={`w-full py-4 rounded-xl font-bold text-lg transition-all duration-300 mt-auto ${
                    isSubscribed || isProcessing || (iosDevice && !appleProducts[mapPlanToProductId(plan, role)] && !iapFinished)
                      ? 'bg-white/10 text-gray-400 cursor-not-allowed border border-white/5'
                      : 'bg-gradient-to-r from-yellow-500 to-amber-600 text-white hover:shadow-[0_0_20px_rgba(234,179,8,0.4)] hover:-translate-y-1'
                  }`}
                >
                  {isSubscribed
                    ? 'Already Subscribed'
                    : isProcessing
                      ? 'Processing...'
                      : (iosDevice && !appleProducts[mapPlanToProductId(plan, role)] && !iapFinished)
                        ? 'Loading Price...'
                        : iosDevice && appleProducts[mapPlanToProductId(plan, role)]
                          ? `Subscribe for ${appleProducts[mapPlanToProductId(plan, role)].localizedPrice}`
                          : `Subscribe for ₹${Math.round(plan.amount * 1.18)}`}
                </button>
              </div>
            );
          })
        )}
      </div>

      {user?.role === 'doctor' && !user.is_blue_tick && !user.blue_tick_requested && (
        <div className="mt-12 bg-blue-900/20 border border-blue-500/30 rounded-3xl p-8 relative overflow-hidden transition-all hover:border-blue-500/50 hover:shadow-[0_0_30px_rgba(59,130,246,0.15)] group relative z-10 max-w-4xl mx-auto">
          <div className="absolute top-0 right-0 w-64 h-64 bg-blue-500/10 rounded-full blur-3xl -mr-20 -mt-20 transition-opacity group-hover:opacity-100 opacity-50"></div>
          <div className="absolute top-0 right-0 p-6">
            <svg className="w-24 h-24 text-blue-500/10 transform rotate-12 group-hover:scale-110 transition-transform duration-500" fill="currentColor" viewBox="0 0 20 20">
              <path d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293l-4 4a1 1 0 01-1.414 0l-2-2a1 1 0 111.414-1.414L9 10.586l3.293-3.293a1 1 0 111.414 1.414z" />
            </svg>
          </div>
          <div className="relative z-10">
            <div className="flex items-center gap-3 mb-4">
              <span className="bg-blue-500 text-white text-[11px] font-bold px-3 py-1 rounded-full uppercase tracking-wider shadow-lg shadow-blue-500/30">Premium</span>
              <h2 className="text-3xl font-black text-white tracking-tight">Blue Tick Certification</h2>
            </div>
            <p className="text-blue-100/70 mb-8 max-w-lg text-lg">
              Enhance your profile with the official blue tick. Gain more trust from patients and stand out as a certified professional.
            </p>
            <div className="flex items-end gap-3 mb-10">
              {iosDevice ? (
                <>
                  <span className="text-4xl font-black text-white">
                    {appleProducts[PRODUCT_IDS.doctor.blueTick]?.localizedPrice || (iapFinished ? `₹${blueTickPrice}` : 'Loading...')}
                  </span>
                  <span className="text-blue-400 text-sm mb-2 font-medium">App Store</span>
                </>
              ) : (
                <>
                  <span className="text-4xl font-black text-white">₹{blueTickPrice}</span>
                  <span className="text-blue-400 text-sm mb-2 font-medium">one-time payment</span>
                </>
              )}
            </div>
            <button
              onClick={handleBlueTickSelect}
              disabled={payingPlanId !== null || (iosDevice && !appleProducts[PRODUCT_IDS.doctor.blueTick] && !iapFinished)}
              className={`font-bold py-4 px-10 rounded-xl transition-all duration-300 text-lg ${
                payingPlanId === 'blueTick' || (iosDevice && !appleProducts[PRODUCT_IDS.doctor.blueTick] && !iapFinished)
                  ? 'bg-white/10 text-gray-400 cursor-not-allowed border border-white/5'
                  : 'bg-gradient-to-r from-blue-600 to-blue-500 text-white hover:shadow-[0_0_20px_rgba(59,130,246,0.4)] hover:-translate-y-1'
              }`}
            >
              {payingPlanId === 'blueTick' ? 'Processing...' : iosDevice && !appleProducts[PRODUCT_IDS.doctor.blueTick] && !iapFinished ? 'Loading Price...' : 'Get Blue Tick Now'}
            </button>
          </div>
        </div>
      )}

      {user?.blue_tick_requested && !user.is_blue_tick && (
        <div className="mt-12 bg-white/5 border border-yellow-500/30 rounded-3xl p-10 text-center backdrop-blur-xl relative z-10 max-w-4xl mx-auto">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-yellow-500/20 mb-6">
            <svg className="w-8 h-8 text-yellow-500 animate-pulse" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <h2 className="text-2xl font-bold text-white mb-3">Verification Pending</h2>
          <p className="text-gray-400 text-lg">Your blue tick request is under review. You will be notified once approved.</p>
        </div>
      )}

      {showPayment && (
        <PaymentGateway
          amount={isBlueTickPayment ? blueTickPrice : selectedPlan?.amount}
          planName={isBlueTickPayment ? "Blue Tick Certification" : selectedPlan?.name}
          userId={userId}
          productId={isBlueTickPayment ? 'com.vivara.healthcare.doctor.bluetick.monthly' : mapPlanToProductId(selectedPlan, role)}
          role={user?.role || role}
          onSuccess={handlePaymentSuccess}
          onError={handlePaymentError}
          onClose={handlePaymentClose}
        />
      )}
    </div>
  );
};

export default Subscription;